import { useTranslation } from "react-i18next";

import { LoadErrorCard, LoadingCard } from "@/components/load-state";
import { Card, CardContent } from "@/components/ui/card";
import { type McpSpeedDay, useAdminMcpSpeed } from "@/lib/api";
import { usePeriod } from "@/lib/period";
import { cn } from "@/lib/utils";

import { EmptyChart, PeriodBar, SectionHeading, StaleNote } from "../parts";
import { loadState } from "../utils";
import { DayBars, type DayBarsSpec } from "./day-bars";
import { SpeedTable } from "./speed-table";

/** The day charts of Snelheid, by their key in `speed.charts`. */
type SpeedChartKey = "waitShare" | "waitP95" | "requestP95" | "rateLimited";

const CHARTS: readonly SpeedChartKey[] = ["waitShare", "waitP95", "requestP95", "rateLimited"];

/** A day's value, or none on a day without Odoo calls. */
function odooDay(day: McpSpeedDay, values: (number | null)[]): number[] | null {
  return day.odoo_calls > 0 && values.every((v) => v !== null) ? (values as number[]) : null;
}

/**
 * Statistieken's Snelheid: over the period, per day, how much of the Odoo
 * calls' time went to waiting (for a free worker, for its turn, for a new
 * attempt), the wait per call and Odoo's slowest request (each the time 95%
 * of the Odoo calls stayed under), and how many calls Odoo slowed down; then
 * the same per tool, in a sortable table with a search. Figures only, with
 * no threshold: a failure is an issue on Fouten.
 */
export function SpeedPage() {
  const { t } = useTranslation();
  const [days, setDays] = usePeriod();
  const speedQuery = useAdminMcpSpeed(days);
  const speed = speedQuery.data;
  const state = loadState(speedQuery);
  const retry = () => void speedQuery.refetch();

  const sampleLabel = t("speed.odooCalls");
  const specs: Record<SpeedChartKey, DayBarsSpec> | undefined = speed && {
    waitShare: {
      unit: "share",
      series: [t("speed.series.worker"), t("speed.series.spacing"), t("speed.series.retry")],
      sampleLabel,
      days: speed.per_day.map((day) => ({
        day: day.day,
        sample: day.odoo_calls,
        values: odooDay(day, [
          day.worker_wait_share ?? 0,
          day.spacing_wait_share ?? 0,
          day.retry_wait_share ?? 0,
        ]),
      })),
    },
    waitP95: {
      unit: "ms",
      series: [t("speed.series.wait")],
      sampleLabel,
      days: speed.per_day.map((day) => ({
        day: day.day,
        sample: day.odoo_calls,
        values: odooDay(day, [day.wait_p95_ms]),
      })),
    },
    requestP95: {
      unit: "ms",
      series: [t("speed.series.request")],
      sampleLabel,
      days: speed.per_day.map((day) => ({
        day: day.day,
        sample: day.odoo_calls,
        values: odooDay(day, [day.request_p95_ms]),
      })),
    },
    rateLimited: {
      unit: "count",
      series: [t("speed.series.rateLimited")],
      sampleLabel: t("usage.calls"),
      days: speed.per_day.map((day) => ({
        day: day.day,
        sample: day.calls,
        values: day.calls > 0 ? [day.rate_limited] : null,
      })),
    },
  };
  const odooCalls = speed?.per_day.reduce((sum, day) => sum + day.odoo_calls, 0) ?? 0;

  return (
    <div className="space-y-8">
      <PeriodBar days={days} onChange={setDays} />
      {speedQuery.isLoading ? (
        <LoadingCard />
      ) : state === "failed" || !speed || !specs ? (
        <LoadErrorCard message={t("speed.loadError")} onRetry={retry} />
      ) : (
        <div
          className={cn(
            "space-y-8 transition-opacity",
            speedQuery.isPlaceholderData && "opacity-60",
          )}
        >
          {state === "stale" && <StaleNote onRetry={retry} />}
          <section aria-labelledby="speed-days" className="space-y-4">
            <SectionHeading id="speed-days">{t("speed.perDay")}</SectionHeading>
            {CHARTS.map((key) => (
              <Card key={key}>
                <CardContent className="space-y-5 py-6 max-sm:px-4">
                  <div>
                    <h3
                      id={`speed-${key}`}
                      className="font-heading text-primary text-base font-bold"
                    >
                      {t(`speed.charts.${key}.title`)}
                    </h3>
                    <p className="text-muted mt-1 text-sm">{t(`speed.charts.${key}.about`)}</p>
                  </div>
                  {odooCalls === 0 && key !== "rateLimited" ? (
                    <EmptyChart>{t("speed.noOdooCalls", { count: speed.days })}</EmptyChart>
                  ) : (
                    <DayBars spec={specs[key]} labelledBy={`speed-${key}`} />
                  )}
                </CardContent>
              </Card>
            ))}
          </section>
          <section aria-labelledby="speed-tools">
            <SectionHeading id="speed-tools">{t("speed.perTool")}</SectionHeading>
            <SpeedTable rows={speed.per_tool} />
          </section>
        </div>
      )}
    </div>
  );
}
